import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { X, Compass, BookOpen, CalendarDays, Sparkles } from 'lucide-react';

/** First-hour welcome nudge for freshly self-signed-up teammates.
 *  Visible for 60 minutes after account creation, on the Home page only.
 *  User can dismiss it manually; otherwise it goes away on its own after the
 *  hour is up. Backend has the authoritative dismiss flag so it survives a
 *  laptop-swap. */
export function WelcomeNudge() {
  const { user, refreshMe } = useAuth();
  const [dismissing, setDismissing] = useState(false);

  const createdAt = user?.created_at ? new Date(user.created_at) : null;
  const ageMinutes = createdAt ? (Date.now() - createdAt.getTime()) / 60000 : 999;
  const withinFirstHour = ageMinutes >= 0 && ageMinutes < 60;
  const alreadyDismissed = !!user?.welcome_dismissed_at;

  // Auto-hide once the hour is up.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!withinFirstHour) return;
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, [withinFirstHour]);
  void tick;

  if (!user) return null;
  if (!withinFirstHour) return null;
  if (alreadyDismissed) return null;

  const minutesLeft = Math.max(1, Math.round(60 - ageMinutes));

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post('/auth/me/dismiss-welcome');
      if (refreshMe) await refreshMe();
    } catch { /* soft-fail — the card will still disappear after the hour */ }
  };

  return (
    <div
      className="mb-6 rounded-xl overflow-hidden border relative
                 bg-gradient-to-br from-[hsl(var(--primary)/.08)] via-[hsl(var(--surface-1))] to-[hsl(var(--surface-1))]"
      style={{ borderColor: 'hsl(var(--primary) / .30)' }}
      data-testid="welcome-nudge"
    >
      <div className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-[hsl(var(--primary))] via-[hsl(var(--primary)/.6)] to-[hsl(var(--primary)/.15)]" />
      <button
        onClick={dismiss}
        disabled={dismissing}
        aria-label="Dismiss welcome"
        data-testid="welcome-nudge-dismiss"
        className="absolute top-2.5 right-2.5 w-7 h-7 grid place-items-center rounded-md text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--primary)/.10)] hover:text-[hsl(var(--foreground))]"
      >
        <X size={14} />
      </button>

      <div className="p-5 pl-6">
        <div className="flex items-center gap-1.5 text-[hsl(var(--primary))] text-[11px] uppercase tracking-wider font-semibold mb-1">
          <Sparkles size={12} /> Welcome
        </div>
        <div className="font-heading text-[22px] leading-tight font-semibold">
          Glad you&apos;re here, {user.name?.split(' ')[0] || 'there'}.
        </div>
        <div className="text-[13.5px] text-[hsl(var(--foreground-muted))] mt-1.5 max-w-[640px]">
          Three quick places to start &mdash; save these to your first hour and you&apos;ll feel at home.
          <span className="ml-1 text-[hsl(var(--foreground-subtle))]">(auto-tidies in {minutesLeft} min)</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
          <NudgeLink to="/how-to" icon={Compass} title="How to use"
            body="A pocket manual for every screen — permissions, buttons, keyboard shortcuts."
            testId="welcome-nudge-howto" />
          <NudgeLink to="/knowledge" icon={BookOpen} title="Knowledge Hub"
            body="SOPs, email templates, and design resources approved by the studio."
            testId="welcome-nudge-knowledge" />
          <NudgeLink to="/my-week" icon={CalendarDays} title="My Week"
            body="Today's commitments, milestones you own, and what's due this week."
            testId="welcome-nudge-my-week" />
        </div>
      </div>
    </div>
  );
}

function NudgeLink({ to, icon: Icon, title, body, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group border rounded-lg px-3.5 py-3 bg-[hsl(var(--card))] hover:border-[hsl(var(--primary)/.55)]
                 transition-colors flex items-start gap-2.5"
      style={{ borderColor: 'hsl(var(--border))' }}
    >
      <div className="w-8 h-8 rounded-md grid place-items-center flex-none bg-[hsl(var(--primary)/.12)] text-[hsl(var(--primary))]">
        <Icon size={15} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold group-hover:text-[hsl(var(--primary))]">{title}</div>
        <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5 line-clamp-2">{body}</div>
      </div>
    </Link>
  );
}
